/**
 * compressImage — reads a File, downscales & JPEG re-encodes via canvas to keep
 * payload small enough for MongoDB/base64 storage while still looking sharp.
 *
 * @param {File} file        - source File object
 * @param {object} opts
 * @param {number} opts.maxWidth   - longest side cap, default 1920
 * @param {number} opts.quality    - JPEG quality 0-1, default 0.85
 * @returns {Promise<{dataUrl:string, originalSize:number, compressedSize:number}>}
 */
export async function compressImage(file, opts = {}) {
  const { maxWidth = 1920, quality = 0.85 } = opts;
  if (!file || !file.type?.startsWith("image/")) {
    throw new Error("Not an image");
  }
  const originalSize = file.size;
  // GIF / SVG — pass through unchanged; canvas would strip animation
  if (file.type === "image/gif" || file.type === "image/svg+xml") {
    const dataUrl = await readAsDataURL(file);
    return { dataUrl, originalSize, compressedSize: originalSize };
  }
  const objectUrl = URL.createObjectURL(file);
  try {
    const img = await loadImage(objectUrl);
    const longestSide = Math.max(img.naturalWidth, img.naturalHeight);
    const scale = longestSide > maxWidth ? maxWidth / longestSide : 1;
    const w = Math.round(img.naturalWidth * scale);
    const h = Math.round(img.naturalHeight * scale);
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, w, h);
    const dataUrl = canvas.toDataURL("image/jpeg", quality);
    const compressedSize = Math.round((dataUrl.length - "data:image/jpeg;base64,".length) * 0.75);
    return { dataUrl, originalSize, compressedSize };
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function readAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}
